import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RequestStatus } from 'legacy/lut/configs/service'
import type { IProgramType } from 'legacy/lut/features/partner360/configuration/models/programType'
import type { ICommitmentData } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentData'
import type { IApiCommitmentStatus } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentStatus'

import { IAsyncDownloadState } from 'partner360/components/asyncContentWrapper/model'
import type { IMappedGeoMappingItem } from 'partner360/components/filters'
import type {
  IAttentionStatusApi,
  TAttentionStatusMode,
} from 'partner360/entities/attentionStatuses'
import { SMBFractionTypes } from 'partner360/entities/planning/model'
import type { IMdfActivity } from 'partner360/features/mdfActivities/model/types'
import type { IProgramOffering } from 'partner360/helpers/offerings/programOfferings'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import type { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import type { IPartnerFlagRequest } from 'partner360/models/partnerFlagRequests/IPartnerFlagRequest'

import type {
  I621Form,
  IFinancialCockpitActualsAndTargetsSetting,
  IFinancialCockpitEltDocuments,
  IFinancialCockpitPendingActionsOffering,
  IFinancialCockpitQbrDocuments,
  IFinancialCockpitUserInfo,
  IFinancialCockpitUserWithScope,
} from '../api/types'
import type { IPendingPbmReplacement } from '../lib'

/** A mapped Fin/SOB row in frontend shape. One row per partner × offering × category ×
 *  fiscal year × fiscal quarter × classification flag. */
export interface IPartnerFinSobRow {
  partnerId: string
  offering: ProgramTypes
  /** Product category, e.g. `jbp-pc-commercial`. `jbp-overall` is the offering total. */
  category: string
  subCategory?: string
  l3Category?: string
  fYear: number
  /** `1`–`4` are quarters; `5` is the full-year total. */
  fQuarter: number
  /** Data classification — only one of the `is*` classification flags is set per row. */
  isActuals: boolean
  isTarget: boolean
  isQuota: boolean
  isDirect?: boolean
  isRollUp?: boolean
  isNoSplit?: boolean
  isPartnerCount?: boolean
  isCollapsed?: boolean
  smbFraction?: SMBFractionTypes | null
  /** Measures. `null` where the payload omitted the column — never coerce to `0`, the cockpit
   *  formatters render `null` as `--`.
   *
   *  `sob` is already a percentage — `42` means 42%, so never multiply by 100. Each `totalSum*`
   *  measure is in USD; its `*Lc` sibling is the same measure in the partner's local currency. */
  sob: number | null
  totalSumNdp: number | null
  totalSumNdpLc: number | null
  totalSumNet: number | null
  totalSumNetLc: number | null
  totalSumTNet: number | null
  totalSumTNetLc: number | null
  totalSumUTNet: number | null
  totalSumUTNetLc: number | null
  finYoyGrowth: number | null
  sobYoyGrowth: number | null
}

export interface IEnrichedCockpitFinSobRow extends IPartnerFinSobRow {
  offeringOrder: number
  categoryOrder: number
  subCategoryOrder: number
  countryCode: string
}

/** Period the dashboard is scoped to. `q1`-`q4` may be combined (multi-select); `fullYear`
 *  and `ytd` are each exclusive with everything else. */
export type CockpitPeriodKey = 'fullYear' | 'q1' | 'q2' | 'q3' | 'q4' | 'ytd'

export type CockpitFiscalQuarter = 1 | 2 | 3 | 4

export type TCockpitRtm = 'resellers' | 'distributors'

export type TMdfStatus = 'All' | 'MDF' | 'Non-MDF'

export type TCockpitPlannedStatus = 'All' | 'Planned' | 'Not Planned'

export type TCockpitMandatoryStatus = 'All' | 'Mandatory' | 'Non-Mandatory'

export const NOT_PART_OF_GLOBAL_PARTNER = '__NOT_PART_OF_GLOBAL_PARTNER__'

export interface ICockpitFilterState {
  geoMappings: string[]
  amplifyMemberships: string[]
  partnerClasses: string[]
  mdfStatus: TMdfStatus
  plannedStatus: TCockpitPlannedStatus
  mandatoryStatus: TCockpitMandatoryStatus
  selectedPartnerIds: string[]
  pbm: string[]
  manager: string[]
  globalPartners: string[]
}

export type ICockpitPlanningPeriod = {
  fYear: number
  fQuarter: CockpitFiscalQuarter
}

/** Which of the Initiatives tile's sub-tabs it shows once expanded: the initiatives themselves,
 *  the MDF activities funding them, or the plan-quality pillars its collapsed score opens. */
export type TCockpitInitiativesView = 'initiatives' | 'mdf' | 'quality'

/** Which content the Financial & SOB tile shows once expanded: its detail table, or the
 *  plan-quality view opened from the collapsed tile's score. */
export type TCockpitRevenueAndSobView = 'detail' | 'quality'

export interface ICockpitKeyInitiative {
  EntryId: number
  /** Partner scope used by the manager cockpit. */
  PartnerId: string
  FYear: number
  /** Empty/null means an offering-independent initiative, visible under every offering. */
  ProgramType: string | null
  /** Creation timestamp — the plan-quality calculation compares it against when the partner's
   *  business priorities were published. */
  Created: string
  Description?: string
  Period: string
  Currency: string
  Name: string
  /** BU Strategic Initiative used by the detailed Initiatives grouping. */
  Development: string
  /** Raw product category key, e.g. `jbp-pc-poly`. */
  BusinessUnit?: string
  BusinessUnitTranslation?: string
  Status?: string
  /** Comma-separated business-priority uids, resolved to names through `jbpSettings`. */
  BusinessPriorities?: string
  GoalNdp?: number
  GoalNet?: number
  GoalTNet?: number
  GoalNdpUSD?: number
  GoalNetUSD?: number
  GoalTNetUSD?: number
  GoalNdpLocalCurrency?: number
  GoalNetLocalCurrency?: number
  GoalTNetLocalCurrency?: number
  IncrementalNdp?: number
  IncrementalNet?: number
  IncrementalTNet?: number
  IncrementalNdpUSD?: number
  IncrementalNetUSD?: number
  IncrementalTNetUSD?: number
  IncrementalNdpLocalCurrency?: number
  IncrementalNetLocalCurrency?: number
  IncrementalTNetLocalCurrency?: number
  HPInvestmentSum?: number
  HPInvestmentSumUSD?: number
  HPInvestmentSource?: string
  DescriptionQuality: string
}

/** One offering from `PartnerData.ProgramOfferingsJson`, parsed. `actualsValueTypes` is the
 *  offering's allowed measures — the options `CockpitMetrics` renders. */
/**
 * An MDF activity in the shared `mdfActivities` domain shape, plus the offering it belongs to.
 *
 * The MDF widget has no such field: it asks the stored procedure for one `ProgramType` and gets
 * a list already scoped to it. The cockpit gets every offering in one payload, so the offering
 * has to ride along for the dashboard's own offering selection to mean anything.
 */
export interface ICockpitMdfActivity extends IMdfActivity {
  /** Offerings the activity belongs to. Plural because its category split can spread across
   *  business units mapping to different offerings — such an activity shows under each.
   *  Empty when the payload states none: then it is never filtered out, so a response without
   *  the field keeps showing everything rather than nothing. */
  programTypes: string[]
}

/** Backend sales for an initiative's product token, repeated when initiatives share a token. */
export interface ICockpitMdfSales {
  EntryId: number
  PartnerId: string
  FYear: number
  ProgramType: string
  BusinessUnit: string
  FocusArea: string
  Token: string
  TokenType: string
  ActualsCY?: number | null
  ActualsCYNet?: number | null
  ActualsCYTNet?: number | null
  ActualsCYUTNet?: number | null
  ActualsCYLC?: number | null
  ActualsCYNetLC?: number | null
  ActualsCYTNetLC?: number | null
  ActualsCYUTNetLC?: number | null
}

export type ICockpitProgramOffering = IProgramOffering

export interface ICockpitCategoryOption {
  category: string
  order: number
}

export type TCockpitCategoriesByOffering = Partial<Record<ProgramTypes, ICockpitCategoryOption[]>>

export interface ICockpitSubCategoryOption {
  subCategory: string
  order: number
}

export type TCockpitSubCategoriesByCategory = Record<string, ICockpitSubCategoryOption[]>

export interface ICockpitPartnerOfferingsCatalog {
  partnerId: string
  offerings: IProgramType[]
}

export interface ICockpitCompletenessItem {
  AcknowledgedDate: string | null
  IsAcknowledged: 0 | 1
  IsPlanned: 0 | 1
  IsRequired: 0 | 1
}

export interface ICockpitCompletenessData {
  PartnerId: string
  Offering: string
  CompletenessData: ICockpitCompletenessItem[]
}

export interface ICockpitPartnerData extends I621Form {
  CompanyEnglishName?: string | null
  AmplifyTrackWithMembership: string
  BitClass?: number
  BitPartnerCategory?: number
  BitSpecialisation?: number
  /** JSON-encoded list of `{ Offering, Status }` — a bit mask over the `AttentionDict`
   *  catalog's `BitValue`s per offering, e.g. `[{"Offering":"Core Print","Status":72}]`. An
   *  offering the list omits raises nothing. Absent from a payload the server has not computed. */
  CockpitAttentionStatus?: string | null
}

export type TCockpitMode = 'partner' | 'manager' | null

/** How the manager cockpit groups the partners behind a status; picked from the tile header. */
export type TCockpitAttentionGrouping = 'market' | 'pbm' | 'manager' | 'partner'

/** One group the attention drill-down walked into, with the grouping it was listed under. */
export interface ICockpitAttentionDrillStep {
  grouping: TCockpitAttentionGrouping
  itemId: string
}

export interface IFinancialCockpitState {
  cockpitMode: TCockpitMode
  /** Invalidation signal that only mounted cockpit views turn into an API request. */
  dataRefreshVersion: number
  /** The offering being switched on for the partner, from the request until its data has been
   *  fetched again; `null` the rest of the time. */
  switchingOnOffering: ProgramTypes | null
  /** The year the board is loading while the previous year's figures stay on screen, and the
   *  request that loads it; `null` the rest of the time. */
  switchingFYear: { fYear: number; requestId: string } | null
  isPartnerDetailsVisible: boolean
  /** Grouping the reader last picked on the level right under a status, kept in the browser.
   *  `null` until they pick one: the role's own default applies. Only the manager cockpit reads it. */
  attentionGrouping: TCockpitAttentionGrouping | null
  /** Grouping picked on the level the drill-down stands on below the first one; `null` keeps the
   *  default that level gets from the step that opened it. */
  attentionLevelGrouping: TCockpitAttentionGrouping | null
  /** Which cockpit the attention drill-down below belongs to. The two catalogs share their bit
   *  values, so without this a status picked in one cockpit resolves in the other. */
  attentionCockpitMode: TAttentionStatusMode | null
  /** `BitValue` of the attention status the expanded tile is showing; `null` shows the list. */
  selectedAttentionStatusBit: number | null
  /** Offering the shown status was raised for. `Overall` lists a row per offering, so the bit
   *  alone does not say which one the detail answers; `null` where no offering states it. */
  selectedAttentionStatusOffering: string | null
  /** What the reader is looking for on the level the drill-down stands on: a partner's name, or
   *  a PBM's name or email. Empty while nothing is being searched for. */
  attentionSearch: string
  /** Groups the manager cockpit's attention drill-down walked into, outermost first. Empty while
   *  the drill-down sits on the level the picked status opens. */
  attentionPath: ICockpitAttentionDrillStep[]
  /** The partner of the manager cockpit's partner level whose status detail is open, where the
   *  reader works the status for that partner. `null` shows the level itself. */
  attentionDetailPartnerId: string | null
  /** Offering of the open partner's status the detail answers; `null` where it states none. */
  attentionDetailOffering: string | null
  /** Whether the manager cockpit's attention tile is open in its full-screen dialog. */
  isAttentionFullscreen: boolean
  /** PBMs and managers reminded from the attention tile in this session, by email. */
  attentionRemindedEmails: string[]
  /** PBM replacements waiting for approval, as asked for in this session or read for a partner. */
  attentionPbmReplacements: IPendingPbmReplacement[]
  finSobData: IPartnerFinSobRow[]
  keyInitiatives: ICockpitKeyInitiative[]
  /** MDF activities of the cockpit's partner, every offering at once. */
  mdfActivities: ICockpitMdfActivity[]
  mdfSales: ICockpitMdfSales[]
  pendingActions: IFinancialCockpitPendingActionsOffering[]
  users: IFinancialCockpitUserWithScope[]
  /** The requesting user's own profile — their role and dashboard scope. */
  userInfo: IFinancialCockpitUserInfo | null
  partnerData: ICockpitPartnerData[]
  geoMappingItems: IMappedGeoMappingItem[]
  partnerFlags: IP360PartnerFlags[]
  partnerFlagRequests: IPartnerFlagRequest[]
  partnerFlagApprovers: string[]
  eltDocuments: IFinancialCockpitEltDocuments[]
  qbrDocuments: IFinancialCockpitQbrDocuments[]
  planningPeriod: ICockpitPlanningPeriod | null
  /** The quarter SOB actuals are collected for: the `jbp-sob-collecting` cycle, picked by its type. */
  sobCollectingPeriod: ICockpitPlanningPeriod | null
  /** Offering the dashboard is scoped to. `null` rolls every offering up together. */
  selectedOffering: ProgramTypes
  /** Category within the selected offering. `null` means all categories. */
  selectedCategory: string | null
  initiativesView: TCockpitInitiativesView
  revenueAndSobView: TCockpitRevenueAndSobView
  selectedPeriods: CockpitPeriodKey[]
  /** Fiscal year the dashboard is scoped to. `null` until the user picks one, at
   *  which point `CockpitFiscalYear` falls back to `selectJBPSettingsCurrentFYear`. */
  selectedFYear: number | null
  /** Whether rows flagged `IsQuota` are shown in the cockpit tables. */
  showQuota: boolean
  offeringsCatalog: ICockpitPartnerOfferingsCatalog[]
  offerings: ICockpitProgramOffering[]
  actualsAndTargetsSettings: IFinancialCockpitActualsAndTargetsSetting[]
  completenessData: ICockpitCompletenessData[]
  commitmentData: ICommitmentData[]
  commitmentStatuses: IApiCommitmentStatus[]
  /** Shared attention-status definitions from the payload's `AttentionDict`. */
  attentionDict: IAttentionStatusApi[]
  selectedMetric: ActualsValueTypes
  selectedRtm: TCockpitRtm
  filter: ICockpitFilterState
  /** Whether the manager cockpit's first load has settled the mandatory filter's default. It is
   *  settled once, so a year switch or a refresh does not overwrite the reader's choice. */
  isMandatoryStatusFilterInitialized: boolean
  /** `'USD'` plus the partner's local currency when it isn't already USD. */
  availableCurrencies: string[]
  selectedCurrency: string
  fetchingStatus: RequestStatus
  error: string | null
  download: IAsyncDownloadState
}
